import { classNames } from '@fullstory/util/fmt';
import React, { forwardRef } from 'react';
import { useIntl } from 'react-intl';
import { identifyComponent } from '../../util';
import { Text, TextAppearance } from '../Text';
import { Tooltip } from '../Tooltip';
import avatarClasses from './avatar.module.scss';
import colorClasses from './colors.module.scss';
import { getAvatarColorFromString } from './getAvatarColorFromString';
import sizeClasses from './sizes.module.scss';
import { AvatarComponentProps, AvatarSizeOptions } from './types';

export const sizeToAppearanceMap: Record<AvatarSizeOptions, TextAppearance> = {
  sm: 'bodyXSmallBold',
  lg: 'bodyMediumBold',
  md: 'bodySmallBold',
};

export const Avatar = forwardRef<HTMLDivElement, AvatarComponentProps>(function Avatar(
  { size = 'md', name, subtitle, disabled, ...nativeProps },
  ref,
) {
  const initial = name.toUpperCase().substring(0, 1);
  const intl = useIntl();

  const tooltipContent = (
    <>
      {name}
      {subtitle && (
        <>
          <br /> {subtitle}
        </>
      )}
    </>
  );

  const colorClass = disabled
    ? avatarClasses.disabled
    : colorClasses[getAvatarColorFromString(name)];

  return (
    // eslint-disable-next-line @fullstory/annotate-react/data-component
    <Tooltip tooltipContent={tooltipContent} asChild placement="bottom" showDelay={300}>
      <div
        ref={ref}
        className={classNames(avatarClasses.avatar, colorClass, sizeClasses[size])}
        data-component="Avatar"
        data-testid="avatar"
        aria-label={intl.formatMessage(
          { defaultMessage: 'Avatar for {name}', id: 'X6KrFx' },
          { name },
        )}
        {...nativeProps}
        {...identifyComponent('avatar', nativeProps)}
      >
        <Text
          appearance={sizeToAppearanceMap[size]}
          tag="span"
          color={disabled ? 'secondary' : 'default'}
        >
          {initial}
        </Text>
      </div>
    </Tooltip>
  );
});

Avatar.displayName = 'Avatar';
